import { LogActionMetadataConfig } from '@blk/aw-bff-typescript-models';
import {
  BULK_SECURITY_ANALYZER_PATH,
  DOWNLOAD_ACTION_MATCH_TERMS,
  EXIT_ACTION_MATCH_TERMS,
  LogAction,
} from '../constants';
import { EventType } from './Models';

declare global {
  interface Window {
    $?: JQueryStatic;
    adobe_report_suite_id?: string;
    globalAnalyticsParams?: GlobalAnalyticsParams;
    OptanonActiveGroups?: string;
    utag?: TealiumAnalytics;
  }
}

export interface TealiumAnalytics {
  link?: (event: LinkEvent) => void;
  view?: (event?: PageViewEvent) => void;
}

export interface GlobalAnalyticsParams {
  advisoryFee?: string;
  analyticsAllowed: string;
  atmosId?: string;
  bookSyncPlatformId?: string;
  country: string;
  enableAbTesting?: string;
  envType: string;
  isInternalTraffic?: string;
  languageSelector: string;
  loggedIn: string;
  padlockedContent: string;
  pageName: string;
  portfolioIds: string;
  siteName: string;
  siteSection: string;
  siteSubSection: string;
  toolName?: string;
  toolState?: string;
  userFirm?: string;
  viewName: string;
  webEntityId?: string;
  webTrackingId: string;
}

export interface LinkEvent {
  link_name: string;
  linkType: LINK_TYPE; // 'o' : general clicks and custom events, 'd': file download, 'e': exit link
}

export interface PageViewEvent {
  page_name: string;
  page_url: string;
}

/**
 * Adobe Analytics specific tracking information for new linkName format
 * Format: {component/field name*} | {sub-field name} | {user-action*} | {label*}
 */
export interface AdobeAnalyticsInfo {
  componentName?: string; // Component or field name - e.g., 'nav', 'banner', 'modal'
  label?: string; // Label - e.g., 'submit-button', 'download-link'
  subFieldName?: string; // Sub-field name (Optional) - sub-wrapper component
  userAction?: AdobeUserActions; // User action - e.g., 'link-click', 'download', 'dismiss', 'submit'
}

// Adobe User Actions
export enum AdobeUserActions {
  CLOSE = 'close',
  COMPLETE = 'complete',
  DISLIKE = 'dislike',
  DISMISS = 'dismiss',
  DOWNLOAD = 'download',
  EMAIL = 'email',
  END = 'end',
  FILTER = 'filter',
  FULLSCREEN = 'fullscreen',
  JUMP_TO = 'jump to',
  LAUNCH = 'launch',
  LIKE = 'like',
  LINK_CLICK = 'link click',
  OPEN = 'open',
  PAUSE = 'pause',
  PLAY = 'play',
  PROMPT = 'prompt',
  RESET = 'reset',
  RESUME = 'resume',
  SEARCH = 'search',
  SHARE = 'share',
  SORT = 'sort',
  START = 'start',
  SUBMIT = 'submit',
}

// Link types for Adobe Analytics
export enum LINK_TYPE {
  DOWNLOAD = 'd',
  EXIT = 'e',
  GENERAL = 'o',
}

/**
 * Mapping of EventType to user action for Adobe Analytics
 */
export const EVENT_TYPE_TO_USER_ACTION: Record<number, string> = {
  [EventType.EVENT_CLICK]: AdobeUserActions.LINK_CLICK,
  [EventType.EVENT_SUBMIT]: AdobeUserActions.SUBMIT,
};

/**
 * Mapping of TrackedAction to user action for Adobe Analytics
 */
export const TRACKED_ACTION_TO_USER_ACTION: Record<string, string> = {
  [LogAction.DOCUMENT_DOWNLOADED]: AdobeUserActions.DOWNLOAD,
  [LogAction.FILTER_BUTTON_CLICKED]: AdobeUserActions.FILTER,
  [LogAction.TAB_CHANGE]: AdobeUserActions.LINK_CLICK,
  [LogAction.EXPORT_TO_EXCEL]: AdobeUserActions.DOWNLOAD,
  [LogAction.REPORT_DOWNLOADED]: AdobeUserActions.DOWNLOAD,
  [LogAction.SETTINGS_OPTION_CLICKED]: AdobeUserActions.LINK_CLICK,
  // Add more mappings as needed
};

// Mapping of user action to search terms for Adobe Analytics
export const USER_ACTION_TO_SEARCH_TERMS: Record<string, string[]> = {
  [AdobeUserActions.DOWNLOAD]: DOWNLOAD_ACTION_MATCH_TERMS,
  [AdobeUserActions.CLOSE]: EXIT_ACTION_MATCH_TERMS,
  // Add more mappings as needed
};

export enum TOOL_NAME {
  // data analytics team requires BSA tool name to match the legacy tool name, aka "tax evaluator"
  BULK_SECURITY_ANALYZER = 'tax evaluator',
  MODEL_EVALUATOR = 'model evaluator',
  PORTFOLIO_ANALYZER = 'portfolio analyzer',
}

// Mapping of site sub-sections to tool names, based on the URL fragment
export const SITE_SUB_SECTION_TO_TOOL_NAME: Record<string, string> = {
  'model-evaluator': TOOL_NAME.MODEL_EVALUATOR,
  comparison: TOOL_NAME.PORTFOLIO_ANALYZER,
  [BULK_SECURITY_ANALYZER_PATH]: TOOL_NAME.BULK_SECURITY_ANALYZER,
  // Add more mappings as needed
};

/**
 * Default mapping of TrackedAction to metadata extraction config
 * These defaults can be overridden via AdobeConfig.logActionMetadataConfigs from the backend
 * - `path`: lodash path to the value in TrackingData
 * - `jsonPath`: if the value at `path` is stringified JSON, this is the nested path to extract after parsing
 */
export const DEFAULT_ACTION_METADATA_CONFIG: Record<string, LogActionMetadataConfig[]> = {
  [LogAction.SET_ADVISORY_FEE]: [{ path: 'elementInfo.metadata', jsonPath: 'advisoryFee' }],
  [LogAction.ADVISORY_FEE_TOGGLE]: [{ path: 'elementInfo.elementValue' }],
  [LogAction.AG_GRID_ROW_GROUP_EXPANDED]: [{ path: 'elementInfo.metadata', jsonPath: 'modelFamily' }],
  [LogAction.AG_GRID_ROW_GROUP_COLLAPSED]: [{ path: 'elementInfo.metadata', jsonPath: 'modelFamily' }],
  [LogAction.MODEL_CHECKBOX_SELECTED]: [{ path: 'elementInfo.metadata', jsonPath: 'modelId' }],
  [LogAction.MODEL_CHECKBOX_DESELECTED]: [{ path: 'elementInfo.metadata', jsonPath: 'modelId' }],
  [LogAction.REPORT_PAGE_GROUP_SELECTION_TOGGLE]: [
    { path: 'elementInfo.metadata', jsonPath: 'groupName' },
    { path: 'elementInfo.elementValue' },
  ],
  [LogAction.GENERIC_FACETED_FILTER_APPLIED]: [{ path: 'elementInfo.metadata', jsonPath: '[*].colTag' }],
  [LogAction.CANCEL_MODAL]: [{ path: 'elementInfo.metadata', jsonPath: 'modalType' }],
};
